import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Cloud, Droplets } from "lucide-react";
import { parkOpenStatus, useLiveHours } from "@/lib/site";

function modeOf(w) {
  if (!w) return "sun";
  if (w.raining === true) return "rain";
  const d = (w?.description || "").toLowerCase();
  return d.includes("cloud") || d.includes("overcast") ? "cloud" : "sun";
}

const SKY = {
  sun: "linear-gradient(180deg, #2a6fc4 0%, #4a8ed9 45%, #7fb4ec 100%)",
  cloud: "linear-gradient(180deg, #3879c6 0%, #5696db 48%, #85b8ee 100%)",
  rain: "linear-gradient(180deg, #2c3644 0%, #46536b 50%, #64748b 100%)",
};

export default function TrafficLightStatus() {
  const hours = useLiveHours();
  const [status, setStatus] = useState(() => parkOpenStatus());
  const [w, setW] = useState(null);
  const canvasRef = useRef(null);
  const mode = modeOf(w);

  useEffect(() => {
    setStatus(parkOpenStatus(new Date(), hours));
    const t = setInterval(() => setStatus(parkOpenStatus(new Date(), hours)), 60000);
    let alive = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/weather`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (alive) setW(d);
      })
      .catch(() => {});
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [hours]);

  // live rain streaks — rain mode only
  useEffect(() => {
    if (mode !== "rain") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = canvas.offsetWidth || 600;
    canvas.height = canvas.offsetHeight || 240;
    const drops = Array.from({ length: 70 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      v: 9 + Math.random() * 7,
      l: 10 + Math.random() * 12,
    }));
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "rgba(205, 224, 245, 0.4)";
      ctx.lineWidth = 1.3;
      for (const p of drops) {
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(p.x - 2, p.y + p.l);
        ctx.stroke();
        p.y += p.v;
        p.x -= 0.5;
        if (p.y > canvas.height) {
          p.y = -14;
          p.x = Math.random() * canvas.width;
        }
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, [mode]);

  const t = w?.temperature_c;
  const desc = w?.description || "—";
  const open = status.open;

  return (
    <div
      className="relative overflow-hidden rounded-3xl p-6 text-white shadow-[0_20px_60px_rgba(36,28,13,0.35)] transition-all duration-1000 sm:p-8"
      style={{ background: SKY[mode] }}
      data-testid="status-indicator-box"
      data-weather-mode={mode}
    >
      {/* Apple-weather style sky — soft sun glow + crisp cloud banks (no blur) */}
      {(mode === "sun" || mode === "cloud") && (
        <>
          <div
            className="pointer-events-none absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2"
            style={{
              background: "radial-gradient(circle, rgba(255,246,220,0.95) 0%, rgba(255,231,166,0.4) 38%, rgba(255,231,166,0) 66%)",
              opacity: mode === "sun" ? 1 : 0.75,
            }}
            data-testid="status-sun"
          />
          <div
            className={`pointer-events-none absolute left-[-12%] w-[80%] ${mode === "cloud" ? "top-2 h-36 opacity-95" : "top-6 h-24 opacity-60"}`}
            style={{
              background: `
                radial-gradient(40% 58% at 20% 62%, rgba(255,255,255,1) 0%, rgba(255,255,255,0.9) 34%, rgba(255,255,255,0) 60%),
                radial-gradient(32% 52% at 44% 40%, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 36%, rgba(255,255,255,0) 62%),
                radial-gradient(36% 50% at 68% 58%, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.8) 34%, rgba(255,255,255,0) 60%),
                radial-gradient(26% 44% at 88% 52%, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.7) 36%, rgba(255,255,255,0) 62%)`,
              animation: "status-cloud-drift 46s linear infinite alternate",
            }}
            data-testid="status-cloud-1"
          />
          <div
            className={`pointer-events-none absolute right-[-8%] w-[65%] ${mode === "cloud" ? "top-16 h-24 opacity-80" : "top-24 h-16 opacity-45"}`}
            style={{
              background: `
                radial-gradient(34% 55% at 30% 55%, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.75) 36%, rgba(255,255,255,0) 62%),
                radial-gradient(30% 48% at 62% 45%, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.7) 38%, rgba(255,255,255,0) 62%)`,
              animation: "status-cloud-drift 62s linear infinite alternate-reverse",
            }}
            data-testid="status-cloud-2"
          />
        </>
      )}
      {mode === "rain" && (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-28 opacity-70"
          style={{
            background: `
              radial-gradient(40% 60% at 25% 45%, rgba(180,196,216,0.6) 0%, rgba(180,196,216,0.4) 40%, rgba(180,196,216,0) 68%),
              radial-gradient(36% 55% at 68% 40%, rgba(180,196,216,0.55) 0%, rgba(180,196,216,0.35) 42%, rgba(180,196,216,0) 68%)`,
            animation: "status-cloud-drift 58s linear infinite alternate",
          }}
          data-testid="status-cloud-3"
        />
      )}
      {/* rain streaks — rain mode */}
      {mode === "rain" && <canvas ref={canvasRef} className="pointer-events-none absolute inset-0 h-full w-full" data-testid="status-rain-canvas" />}

      {/* techy glass readout */}
      <div className="relative rounded-2xl border border-white/25 bg-black/40 p-6 backdrop-blur-md sm:p-8" data-testid="status-glass">
        <p className="text-[11px] font-black uppercase tracking-[0.3em] text-[#f8b818]">Live from the park</p>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <p className="text-7xl leading-none text-[#f8b818] drop-shadow-[0_2px_0_rgba(122,86,0,0.7)] sm:text-8xl" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }} data-testid="status-temp">
            {t != null ? `${Math.round(t)}°C` : "—"}
          </p>
          <span
            className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-5 py-2 text-lg font-extrabold capitalize text-white"
            data-testid="status-desc"
          >
            {desc}
          </span>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2.5">
          {w?.humidity_pct != null && (
            <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-extrabold" data-testid="status-humidity">
              <Droplets size={15} className="text-[#7ec8f5]" /> Humidity {Math.round(w.humidity_pct)}%
            </span>
          )}
          {w?.clouds_pct != null && (
            <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-extrabold" data-testid="status-clouds">
              <Cloud size={15} /> Clouds {Math.round(w.clouds_pct)}%
            </span>
          )}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2.5 border-t border-white/20 pt-4">
          {/* the park's robot — green when open, red when closed (official artwork) */}
          <img
            src={open ? "/traffic-light-green.png" : "/traffic-light-red.png"}
            alt={open ? "Traffic light on green — the park is open" : "Traffic light on red — the park is closed"}
            className="h-24 w-auto drop-shadow-[0_8px_20px_rgba(0,0,0,0.5)]"
            data-testid="status-robot"
          />
          <span
            className={`inline-flex items-center gap-2 rounded-full border border-white/25 bg-black/40 px-4 py-1.5 text-xs font-extrabold uppercase tracking-widest ${open ? "text-[#7ef0a8]" : "text-[#ff9d9d]"}`}
            data-testid="status-badge"
          >
            <span
              className={`h-2.5 w-2.5 rounded-full ${open ? "bg-[#22c55e] shadow-[0_0_10px_rgba(34,197,94,0.9)] animate-pulse" : "bg-[#ef4444] shadow-[0_0_10px_rgba(239,68,68,0.9)] animate-pulse"}`}
            />
            {open ? "Open now" : "Closed"}
          </span>
          <span className="text-sm font-bold text-white/90" data-testid="status-hours">
            {status.isSchoolHoliday
              ? `School holidays · open ${hours.weekend_open} – ${hours.weekend_close} daily`
              : status.isPublicHoliday
                ? `Public holiday · open ${hours.weekend_open} – ${hours.weekend_close} today`
                : `Mon–Fri ${hours.weekday_open} – ${hours.weekday_close} · Weekends, Public- & School Holidays ${hours.weekend_open} – ${hours.weekend_close}`}
          </span>
          <Link
            to="/times#schedule"
            className="text-xs font-extrabold uppercase tracking-widest text-[#f8b818] underline-offset-4 hover:underline"
            data-testid="status-schedule-link"
          >
            Opening times ↓
          </Link>
        </div>
        {!open && <p className="mt-2 text-xs font-bold text-white/80">{status.label}</p>}
      </div>
    </div>
  );
}
